import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";

import { ACCOUNT_CAPABILITY_KEYS } from "../constants/verification.constants";
import { ensureAccountVerification, peekAccountVerification } from "../services/accountVerification.memo";
import type {
  AccountCapabilityKey,
  AccountVerificationResponse,
  KycStatus,
  VerificationNextStep,
  VerificationPurpose,
} from "../types/verification-api.types";

export interface OrganizationVerificationStatus {
  isLoading: boolean;
  /** Only organization members verify this way; internal staff have no organization of their own. */
  isOrgMember: boolean;
  /** The server verdict. `null` until resolved, or when it could not be resolved (fail-safe). */
  data: AccountVerificationResponse | null;
  kycStatus?: KycStatus;
  identityId?: string;
  /**
   * KYC (natural person) or KYB (company) — the organization's choice. `null` until the corporate identity
   * exists.
   */
  purpose: VerificationPurpose | null;
  hasCorporateIdentity: boolean;
  /**
   * `capabilities["payouts.create"].allowed`. FAIL-OPEN (`true`) while unknown: the backend 403 is the real
   * gate, and a network hiccup must not lock a verified organization out of the wizard.
   */
  canCreatePayouts: boolean;
  blockedCapabilities: AccountCapabilityKey[];
  nextStep: VerificationNextStep;
  /** Coloca i18n key or legacy raw text — resolve with `resolveRejectionDescription`. */
  rejectionReason?: string;
  /** Nudge flag for the Wallets banner: resolved and not VERIFIED. */
  shouldVerify: boolean;
  /** The last read failed and nothing is memoized. */
  hasError: boolean;
  /** Re-read, bypassing the memo. */
  refresh: () => void;
}

interface VerdictState {
  key: string | null;
  data: AccountVerificationResponse | null;
}

/**
 * The current organization's own verification (KYC or KYB) and the capabilities it unlocks, from `GET
 * /internal/account/verification`.
 *
 * - The server is the source of truth: the capability matrix and the payouts gate are computed there from one
 *   policy map, so this hook never derives "can operate" from a status on its own.
 * - Memoized per `${user.id}:${orgId}` (`accountVerification.memo`) and seeded synchronously, so the Wallets
 *   banner, the Settings section and the dispersions gate share one request and a second mount does not
 *   flash.
 * - Only organization members are read; internal staff have no organization and get an inert result.
 * - Fail-safe: a failed read leaves `data` null, `shouldVerify` false and `canCreatePayouts` true — the backend
 *   still refuses a real payout with a coded 403 that the wizard renders.
 */
export function useOrganizationVerificationStatus(): OrganizationVerificationStatus {
  const { user, isInternalUser } = useAuth();
  const orgId = user?.organization?.id ?? user?.organization_id;
  const isOrgMember = !isInternalUser && Boolean(orgId);
  const key = isOrgMember && user ? `${user.id}:${orgId}` : null;

  // The verdict is stored WITH the key it answers for, so a key change (another session in the same mount)
  // never shows the previous organization's verdict while the new one loads.
  const [state, setState] = useState<VerdictState>(() => ({
    key,
    data: key ? peekAccountVerification(key) : null,
  }));
  const data = state.key === key ? state.data : key ? peekAccountVerification(key) : null;

  const [isLoading, setIsLoading] = useState(() => key !== null && peekAccountVerification(key) === null);
  const [hasError, setHasError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  // `refresh` bypasses the memo once; a later key change goes back to the memo.
  const forceRef = useRef(false);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const force = forceRef.current;
    forceRef.current = false;
    if (force || peekAccountVerification(key) === null) {
      // eslint-disable-next-line react-hooks-eslint/set-state-in-effect
      setIsLoading(true);
    }
    ensureAccountVerification(key, force)
      .then((verdict) => {
        if (cancelled) return;
        setState({ key, data: verdict });
        setHasError(false);
      })
      .catch(() => {
        // Can't determine the status — keep whatever we had, and don't nag.
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, reloadToken]);

  const refresh = useCallback(() => {
    forceRef.current = true;
    setReloadToken((n) => n + 1);
  }, []);

  const payouts = data?.capabilities["payouts.create"];
  const canCreatePayouts = payouts ? payouts.allowed : true;
  const blockedCapabilities = data
    ? ACCOUNT_CAPABILITY_KEYS.filter((capability) => data.capabilities[capability]?.allowed === false)
    : [];

  return {
    isLoading,
    isOrgMember,
    data,
    kycStatus: data?.kyc_status,
    identityId: data?.identity_id ?? undefined,
    purpose: data?.verification.purpose ?? null,
    hasCorporateIdentity: data?.has_corporate_identity ?? false,
    canCreatePayouts,
    blockedCapabilities,
    nextStep: data?.verification.next_step ?? "none",
    rejectionReason: data?.rejection_reason ?? undefined,
    shouldVerify: isOrgMember && data !== null && data.kyc_status !== "VERIFIED",
    hasError: hasError && data === null,
    refresh,
  };
}
